import React from 'react';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { ConditionalCreateSpaceDrawer } from '@confluence/create-space/entry-points/createSpace.ts';
import {
	ConditionalImportContentModal,
	ConditionalImportContentDrawer,
} from '@confluence/import-content/entry-points/importContent.ts';
import { ForgeGlobalKeyboardShortcuts } from '@confluence/forge-ui/entry-points/ForgeGlobalKeyboardShortcuts.ts';
import { StorageEnforcement } from '@confluence/storage-enforcement/entry-points/StorageEnforcement.ts';
import { LoomAiMeetingNotesPostProvisionFlag } from '@confluence/experiment-loom-ai-meeting-notes-in-confluence-cross-flow/entry-points/LoomAiMeetingNotesPostProvisionFlag.ts';
import { TrialFlags } from '@confluence/trial-components/entry-points/TrialFlags.tsx';
import { LazyRestrictionsDialogMountPoint } from '@confluence/restrictions/entry-points/RestrictionsDialogTrigger.ts';
import { ProgressBar } from '@confluence/global-progress-bar';
import { useAddUFOCustomDataForNavType } from '@confluence/nav4-enabled/entry-points/useAddUFOCustomDataForNavType.ts';
import { useIsNav4Enabled } from '@confluence/nav4-enabled/entry-points/useIsNav4Enabled.ts';
import { MisalignedNavVersionsFlag } from '@confluence/nav-versions-misaligned';
import { TrialPlanComparisonModalMountComponent } from '@confluence/persistent-upgrade/entry-points/lazyTrialPlanComparisonModal.ts';
import { StandardToPremiumModalMountComponent } from '@confluence/experiment-edition-awareness-for-standard-to-premium';
import { ModernizeChangeboarding } from '@confluence/modernize-changeboarding';
import { NthUserOrientation } from '@confluence/experiment-nth-user-orientation/entry-points/NthUserOrientation.ts';
import { ConfluenceSmartCreationModalLoader } from '@confluence/smart-create/entry-points/smartCreationModal.tsx';
import { FloatingAudioContainer } from '@confluence/audio/entry-points/FloatingAudioContainer.ts';
import { CrossJoinNudgesWithFlags } from '@confluence/cross-join-nudges';
import { fg } from '@confluence/feature-gating';
import { UpgradeFreePoolExperimentModalPostUpgradeModalLoadable } from '@confluence/experiment-upgrade-free-pool-target/entry-points/post-upgrade-modal.tsx';
import { UpgradeFreePoolExperimentModalPreUpgradeModalLoadable } from '@confluence/experiment-upgrade-free-pool-target/entry-points/pre-upgrade-modal.tsx';
import { EndOfTrialModalContainerWithErrorBoundaryLoadable } from '@confluence/experiment-ai-in-editor-eot/entry-points/end-of-trial-modal.ts';
import { RovoAnnouncementSplashModal } from '@confluence/create-with-rovo-announcement-splash/entry-points/RovoAnnouncementSplashModal.ts';

/**
 * Use this component to render global components that should be rendered on every page.
 */
export const GlobalComponents = () => {
	const isNav4DisplayedToUser = useIsNav4Enabled();

	useAddUFOCustomDataForNavType();

	return (
		<>
			<ErrorBoundary
				attribution={Attribution.DISCO}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Disco',
				}}
			>
				<ConditionalCreateSpaceDrawer />
				<MisalignedNavVersionsFlag />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.SMARTS}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Smarts',
				}}
			>
				{fg('aifc_create_enabled') && <ConfluenceSmartCreationModalLoader />}
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.CC_IMPORTER}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Importer',
				}}
			>
				<ConditionalImportContentModal />
				<ConditionalImportContentDrawer />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.ECOSYSTEM}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Ecosystem',
				}}
			>
				<ForgeGlobalKeyboardShortcuts />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.ADMIN_EXPERIENCE}
				attributes={{
					errorBoundaryId: 'GlobalComponents-AdminExperience',
				}}
			>
				<StorageEnforcement />
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.GROWTH_LUNA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthLuna',
				}}
			>
				<TrialPlanComparisonModalMountComponent />
				<StandardToPremiumModalMountComponent />
				<TrialFlags />
			</ErrorBoundary>

			<ErrorBoundary attribution={Attribution.PERMISSIONS_EXPERIENCE}>
				<LazyRestrictionsDialogMountPoint />
			</ErrorBoundary>

			<ErrorBoundary attribution={Attribution.FLYWHEEL_ACTIVATION}>
				<ModernizeChangeboarding />
				<NthUserOrientation />
			</ErrorBoundary>

			{fg('aifc_onboarding_ga_enabled') && (
				<ErrorBoundary attribution={Attribution.FLYWHEEL_FEATURE_DISCOVERY}>
					<RovoAnnouncementSplashModal />
				</ErrorBoundary>
			)}

			{isNav4DisplayedToUser && (
				<ErrorBoundary attribution={Attribution.BACKBONE}>
					<ProgressBar isNav4 />
				</ErrorBoundary>
			)}

			<ErrorBoundary
				attribution={Attribution.GROWTH_PUMA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthPuma',
				}}
			>
				<CrossJoinNudgesWithFlags />
			</ErrorBoundary>

			{fg('loom_ai_meeting_notes_in_confluence_killswitch') && (
				<ErrorBoundary attribution={Attribution.GROWTH_MULTIPLIERS}>
					<LoomAiMeetingNotesPostProvisionFlag />
				</ErrorBoundary>
			)}

			<ErrorBoundary
				attribution={Attribution.GROWTH_LUNA}
				attributes={{
					errorBoundaryId: 'GlobalComponents-GrowthArtemis',
				}}
			>
				{fg('twcg-206_confluence_upgrade_permanent_free_fg') && (
					<>
						<UpgradeFreePoolExperimentModalPreUpgradeModalLoadable />
						<UpgradeFreePoolExperimentModalPostUpgradeModalLoadable />
					</>
				)}
				{fg('twcg-530_confluence_ai_in_editor_eot_gate') && (
					<EndOfTrialModalContainerWithErrorBoundaryLoadable />
				)}
			</ErrorBoundary>

			<ErrorBoundary
				attribution={Attribution.SMARTS}
				attributes={{
					errorBoundaryId: 'GlobalComponents-Audio',
				}}
			>
				<FloatingAudioContainer />
			</ErrorBoundary>
		</>
	);
};
